import React, { useCallback, useMemo, useState } from "react";

import {
  Autocomplete,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  Stack,
  TextField,
  TextFieldProps,
  Typography
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { SimpleHead } from "components";
import { DateRangeSelector } from "components/data-rooms";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { useDidUpdate } from "hooks";
import { startCase } from "lodash";
import { useDateFormatSelect, useInvestorsSelector, useTaskDealsSelect } from "store";
import { DealBase, FileRepoFilters, IInvestor } from "store/types";

import { fileSources, FileType } from "../../../constants";
import { defaultFileFilters } from "../../../constants/file";

const fileTypeOptions = Object.values(FileType).filter(
  (ft) => !["form", "email", "note", "deal", "file", "document", "folder"].includes(ft)
);

const commonInputProps: Pick<TextFieldProps, "sx"> = {
  sx: { ".MuiInputBase-root": { borderRadius: "8px" } }
};

interface Props {
  onClose: () => void;
  isOpen: boolean;
  filters: FileRepoFilters;
  setFilters: (data: Partial<FileRepoFilters>) => void;
  documentTypes: string[];
}

type SetDate = ({
  incomingDate,
  field,
  limit
}: {
  incomingDate: string | null;
  field: keyof Pick<FileRepoFilters, "date">;
  limit: "from" | "to";
}) => void;

export const FiltersDrawer: React.FC<Props> = ({ isOpen, onClose, filters, setFilters, documentTypes }) => {
  // const rounds = useRoundsSelector();
  // const investors = useDealInvestorsSelector();
  const investors = useInvestorsSelector();
  const dateFormat = useDateFormatSelect();
  const deals = useTaskDealsSelect();
  const [selectedDateRange, setSelectedDateRange] = useState<number>(-1);

  // const roundsOptions = useMemo(() => rounds.map(({ id, name }) => ({ id, name })), [rounds]);
  const investorsOptions = useMemo(() => investors.map(({ id, name }) => ({ id, name })), [investors]);

  const [enableReinitialize, setEnableReinitialize] = useState<boolean>(true);

  useDidUpdate(() => {
    // Return back reinitilzation after clear all filters
    if (isOpen && !enableReinitialize) {
      setEnableReinitialize(true);
    }
  }, [isOpen]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { ...rest } = filters;

  const close = () => {
    onClose();
    setSelectedDateRange(-1);
  };

  const { values, setFieldValue, handleSubmit, resetForm } = useFormik({
    initialValues: { ...rest },
    onSubmit: (result) => {
      setFilters({ ...result });
      setEnableReinitialize(true);
      close();
    },
    enableReinitialize: enableReinitialize
  });

  const setDate: SetDate = useCallback(
    ({ incomingDate, field, limit }) => {
      const currentValue = values[field];

      const date = incomingDate ? new Date(incomingDate.valueOf()) : null;

      if (!date && currentValue?.[limit]) {
        // Delete tate when it comes null
        delete currentValue[limit];
        return setFieldValue(field, currentValue);
      }

      if (currentValue && date) {
        const updatedValue = { ...currentValue, [limit]: new Date(date.valueOf()) };
        return setFieldValue(field, updatedValue);
      }

      if (!currentValue && date) {
        return setFieldValue(field, { [limit]: new Date(date.valueOf()) });
      }
    },
    [values, setFieldValue]
  );

  const handleDateRangeChange = (value: number) => {
    let startDate = dayjs().startOf("day");
    let endDate = dayjs().endOf("day");

    if (value > 1) {
      startDate = dayjs()
        .subtract(value - 1, "days")
        .startOf("day");
    }

    startDate = startDate.startOf("day");
    endDate = endDate.startOf("day");

    const fromFormatted = `${startDate.year()}-${(startDate.month() + 1).toString().padStart(2, "0")}-${startDate
      .date()
      .toString()
      .padStart(2, "0")}T00:00:00Z`;
    const toFormatted = `${endDate.year()}-${(endDate.month() + 1).toString().padStart(2, "0")}-${endDate
      .date()
      .toString()
      .padStart(2, "0")}T23:59:59Z`;

    setFieldValue("date", { from: fromFormatted, to: toFormatted });
    setSelectedDateRange(value);
  };

  const clearForm = useCallback(() => {
    setEnableReinitialize(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { ...rest } = defaultFileFilters;
    resetForm({ values: rest });
  }, []);

  // const transformedFunds = fundFilesTypes.map((fund, index) => ({
  //   id: index + 1,
  //   name: fund
  // }));

  return (
    <Drawer
      sx={{
        form: {
          minWidth: 430,
          maxWidth: 430,
          height: "100%",
          display: "flex",
          flexDirection: "column"
        },
        ".MuiPaper-root": { borderRadius: "0 20px 20px 0" }
      }}
      open={isOpen}
      onClose={close}
    >
      <form onSubmit={handleSubmit}>
        <SimpleHead onIconClick={close}>Filter</SimpleHead>
        <Stack
          sx={{
            flexGrow: 1,
            gap: "24px",
            overflowY: "auto",
            p: "18px 30px"
          }}
        >
          <Autocomplete
            disableCloseOnSelect
            options={fileTypeOptions}
            getOptionLabel={(option) => option.toUpperCase()}
            value={values.file_types}
            multiple
            onChange={(_, option) => setFieldValue("file_types", option)}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: string) => (
              <li {...props} key={option}>
                {option.toUpperCase()}
              </li>
            )}
            renderInput={(params) => <TextField {...params} {...commonInputProps} label="Type" />}
          />
          <Autocomplete
            disableCloseOnSelect
            value={values.deals}
            onChange={(_, option) => setFieldValue("deals", option)}
            options={deals}
            multiple
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: DealBase) => (
              <li {...props} key={option.id}>
                {option.title}
              </li>
            )}
            renderInput={(params) => <TextField {...params} {...commonInputProps} label="Deal" />}
            getOptionLabel={(option) => option.title}
          />
          {/* <Autocomplete
            options={roundsOptions}
            value={values.rounds}
            getOptionLabel={(option) => option.name}
            multiple
            renderOption={(
              props: React.HTMLAttributes<HTMLLIElement>,
              option: Partial<Pick<IRound, "id" | "name">>
            ) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
            onChange={(_, option) => setFieldValue("rounds", option)}
            renderInput={(params) => <TextField {...params} {...commonInputProps} label="Round" />}
          /> */}
          <Autocomplete
            disableCloseOnSelect
            options={investorsOptions}
            value={values.investors}
            getOptionLabel={(option) => option.name}
            multiple
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: Pick<IInvestor, "id" | "name">) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
            onChange={(_, option) => setFieldValue("investors", option)}
            renderInput={(params) => <TextField {...params} {...commonInputProps} label="Investor" />}
          />
          <Autocomplete
            disableCloseOnSelect
            options={documentTypes}
            getOptionLabel={(option) => option}
            value={values.document_types}
            multiple
            onChange={(_, option) => setFieldValue("document_types", option)}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: string) => (
              <li {...props} key={option}>
                {startCase(option)}
              </li>
            )}
            renderInput={(params) => <TextField {...params} {...commonInputProps} label="Altius Classification" />}
          />
          <Autocomplete
            disableCloseOnSelect
            options={fileSources}
            getOptionLabel={(option) => option}
            value={values.source}
            multiple
            onChange={(_, option) => setFieldValue("source", option)}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: string) => (
              <li {...props} key={option}>
                {startCase(option)}
              </li>
            )}
            renderInput={(params) => <TextField {...params} {...commonInputProps} label="Source" />}
          />
          <Stack direction="row" sx={{ justifyContent: "space-between" }}>
            <DateRangeSelector value={selectedDateRange} onChange={handleDateRangeChange} />
            <FormControlLabel
              sx={{ minWidth: "162px" }}
              control={
                <Checkbox
                  checked={values.show_latest.valueOf()}
                  onChange={(e) => setFieldValue("show_latest", e.currentTarget.checked)}
                  name="Show Latest"
                />
              }
              label="Show Latest"
            />
          </Stack>
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: "16px" }}>
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              value={values.date?.from ? dayjs(values.date.from) : null}
              label="From"
              onChange={(date) => {
                if (date && date.isValid()) {
                  setDate({ incomingDate: date && date?.toISOString(), field: "date", limit: "from" });
                }
              }}
              slotProps={{
                textField: { placeholder: "Select" },
                inputAdornment: { position: "start" },
                popper: {
                  sx: {
                    ".MuiPickersCalendarHeader-labelContainer": {
                      fontSize: "0.9rem"
                    }
                  }
                }
              }}
            />
            -
            <DesktopDatePicker
              format={dateFormat}
              views={["year", "month", "day"]}
              value={values.date?.to ? dayjs(values.date.to) : null}
              label="To"
              onChange={(date) => {
                if (date && date.isValid()) {
                  setDate({ incomingDate: date && date.toISOString(), field: "date", limit: "to" });
                }
              }}
              slotProps={{
                textField: { placeholder: "Select" },
                inputAdornment: { position: "start" },
                popper: {
                  sx: {
                    ".MuiPickersCalendarHeader-labelContainer": {
                      fontSize: "0.9rem"
                    }
                  }
                }
              }}
            />
          </Stack>
        </Stack>
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 -1px 5px 1px rgba(0,0,0,.08)",
            p: "18px 30px"
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              textDecoration: "underline",
              fontWeight: 500,
              cursor: "pointer"
            }}
            onClick={clearForm}
          >
            Clear All Filters
          </Typography>
          <Stack direction="row" gap="18px">
            <Button variant="outlined" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              Apply
            </Button>
          </Stack>
        </Stack>
      </form>
    </Drawer>
  );
};
